import * as React from 'react';
import {
  Box,
  Container,
  Grid2 as Grid,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SecurityIcon from '@mui/icons-material/Security';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionCard = motion(Card);
const MotionButton = motion(Button);

interface WhyLawFirmsSectionProps {
  scrollToContact?: () => void;
}

const WhyLawFirmsSection = React.forwardRef<
  HTMLDivElement,
  WhyLawFirmsSectionProps
>(({ scrollToContact }, ref) => {
  const theme = useTheme();

  const reasons = [
    {
      title: 'Reduce Administrative Overhead',
      description:
        'Automate manual processes and reduce time spent on administrative tasks by up to 70%, freeing your team to focus on legal strategy.',
      icon: (
        <SpeedIcon
          fontSize="large"
          sx={{ color: theme.palette.primary.main }}
        />
      ),
    },
    {
      title: 'Maximize Settlement Participation',
      description:
        'Boost claim submission rates with automated reminders and simplified processes, resulting in higher overall settlement participation.',
      icon: (
        <AttachMoneyIcon
          fontSize="large"
          sx={{ color: theme.palette.primary.main }}
        />
      ),
    },
    {
      title: 'Enhance Client Engagement',
      description:
        'Build stronger relationships with class members through transparent communication and regular updates on case progress.',
      icon: (
        <PeopleAltIcon
          fontSize="large"
          sx={{ color: theme.palette.primary.main }}
        />
      ),
    },
    {
      title: 'Secure and Compliant',
      description:
        'Rest easy with bank-level security protocols and GDPR/CCPA compliance to protect sensitive client data and maintain regulatory standards.',
      icon: (
        <SecurityIcon
          fontSize="large"
          sx={{ color: theme.palette.primary.main }}
        />
      ),
    },
  ];

  // Animation variants matching HowItWorksSection
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.default',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          sx={{ textAlign: 'center', mb: 6 }}
        >
          <MotionTypography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 2,
            }}
            variants={itemVariants}
          >
            Why Law Firms Use ClassAct
          </MotionTypography>
          <MotionTypography
            variant="h6"
            sx={{ maxWidth: 700, mx: 'auto', color: 'text.secondary' }}
            variants={itemVariants}
          >
            Streamline your class action management with purpose-built tools
            that enhance efficiency and improve client satisfaction.
          </MotionTypography>
        </MotionBox>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {reasons.map((reason, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6 }}>
              <MotionCard
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.10)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.16)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {reason.icon}
                    <Typography
                      variant="h6"
                      sx={{
                        mt: 2,
                        mb: 1.5,
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      {reason.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                    >
                      {reason.description}
                    </Typography>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Button */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 6,
          }}
        >
          <MotionButton
            variant="contained"
            color="primary"
            onClick={scrollToContact}
            sx={{
              borderRadius: '20px',
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              height: '40px',
              minWidth: '130px',
              whiteSpace: 'nowrap',
            }}
            variants={buttonVariants}
          >
            Book a Demo
          </MotionButton>
        </MotionBox>
      </Container>
    </Box>
  );
});

export default WhyLawFirmsSection;
